/**
 * The injected report script (`apps/ara` report.py) mutates the live DOM to
 * deliver the edit surface: it toggles `ara-picker-selected` / `ara-picker-hover`
 * outlines on elements, marks editables with `contenteditable`, paints the
 * `ara-editable-leaf` affordance onto keyless text leaves, and floats an
 * `.ara-target-actions` toolbar. Those are UI-transient artifacts, but the
 * debounced `sectionEdit` message posts the raw `.ara-section-body` innerHTML
 * — so a capture taken while an element is selected carries the selection
 * outline class into the stored section. From there it is baked into every
 * downstream render (preview refresh, source PUT, and the PUBLISHED snapshot),
 * where the outline style still ships in the document `<style>`: the published
 * ARA shows a blue "selected element" border (FE-5412 bug).
 *
 * Stripping at this intake seam keeps the section truth clean, so every
 * consumer (sync, publish, re-render, edit-chat scoping) reads artifact-free
 * HTML regardless of what the iframe happened to have selected.
 */

/**
 * Classes the edit surface toggles on the live DOM: the picker outlines and
 * the `ara-editable-leaf` affordance mark edit mode paints onto keyless text
 * leaves. All are UI-transient and must never reach a stored section.
 */
const EDIT_CLASSES = ['ara-picker-selected', 'ara-picker-hover', 'ara-picker-col', 'ara-editable-leaf']

/** Floating UI the script appends (toolbar lives on <body>, but strip defensively). */
const ARTIFACT_NODE_SELECTOR = '.ara-target-actions, .sec-edit-btn'

/**
 * Remove edit-surface artifacts from a captured HTML fragment. Returns the
 * original string when nothing had to be stripped (cheap identity for the
 * common case of a clean capture).
 */
export function stripAraEditArtifacts(html: string): string {
  if (!html) return html
  const container = document.createElement('div')
  container.innerHTML = html
  let changed = false

  for (const node of container.querySelectorAll(ARTIFACT_NODE_SELECTOR)) {
    node.remove()
    changed = true
  }
  for (const el of container.querySelectorAll('[class]')) {
    let removed = false
    for (const token of EDIT_CLASSES) {
      if (el.classList.contains(token)) {
        el.classList.remove(token)
        removed = true
      }
    }
    if (removed) {
      if (el.getAttribute('class') === '') el.removeAttribute('class')
      changed = true
    }
  }
  for (const el of container.querySelectorAll('[contenteditable]')) {
    el.removeAttribute('contenteditable')
    changed = true
  }

  return changed ? container.innerHTML : html
}
